/**
 * useHasExistingChallenges
 * Lightweight probe answering "does this signed-in user already have at least one
 * challenge (PENDING / ACTIVE / COMPLETED)?". Used to gate one-time onboarding
 * (create walkthrough, World Cup welcome) so established users never see the intros.
 *
 * Fetches a single row (limit=1) rather than the full Arena walk, so it resolves
 * fast and independently of the heavier active-challenges query.
 *
 * Failure mode is deliberate: on error/timeout we report `hasChallenges: true`
 * (treat as established → suppress onboarding). The reported bug is established
 * users wrongly seeing onboarding; the safe failure is to suppress, not to show.
 */

import { useQuery } from '@tanstack/react-query'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { fetchChallengesPage } from '../helpers/challengesApi'
import {
  ARENA_STATUS_FILTER,
  CHALLENGES_STALE_TIME_MS,
  CHALLENGES_GC_TIME_MS,
} from '../constants/challenges.constants'
import { challengesQueryKeys } from '../constants/challengesQueryKeys'

interface UseHasExistingChallengesResult {
  /** True once we have a definitive answer (success, error, or query not applicable). */
  isResolved: boolean
  /** True if the user has ≥1 challenge — or if we couldn't tell (error → default to suppress onboarding). */
  hasChallenges: boolean
}

export function useHasExistingChallenges(): UseHasExistingChallengesResult {
  const apiClient = useApiClient()
  const { userId, isSignedIn, isAnonymous, isLoaded } = useSupabaseAuth()

  const queryEnabled =
    isLoaded && isSignedIn === true && !isAnonymous && !!userId

  const { data, isSuccess, isError } = useQuery({
    queryKey: challengesQueryKeys.hasAny(userId ?? null),
    queryFn: async (): Promise<boolean> => {
      const { data: rows, count } = await fetchChallengesPage(
        apiClient,
        ARENA_STATUS_FILTER,
        1,
        0,
      )
      return count > 0 || rows.length > 0
    },
    enabled: queryEnabled,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })

  // When the query never runs (guest / not yet signed in), there is nothing to
  // wait on: report resolved with no challenges so guests still get onboarding.
  if (!queryEnabled) {
    return { isResolved: true, hasChallenges: false }
  }

  return {
    isResolved: isSuccess || isError,
    hasChallenges: isError ? true : data === true,
  }
}
